// Single mount point (DashboardLayout, behind isOfflineModeEnabled()) for the
// offline session chrome: the inactivity/trust-window lock, the logout
// confirmation dialog, and the "not available offline" notification. Also
// runs the 24h trust-window ticker and restores a persisted lock on reload.

import { useEffect, useState } from 'react';
import Notification from '@prism-ehr/shared/components/ui/Notification';
import { connectivityMonitor } from '@/services/offline/connectivityMonitor';
import { useOfflineSessionStore } from '@/services/offline/offlineSessionStore';
import { lockIfOfflineMode, isLockPersisted } from '@/services/offline/offlineSession';
import { authService } from '@/services/auth';
import OfflineLockScreen from './OfflineLockScreen';
import OfflineLogoutConfirmDialog from './OfflineLogoutConfirmDialog';

const TRUST_WINDOW_MS = 24 * 60 * 60 * 1000;
const TICK_MS = 60000;

export default function OfflineSessionManager() {
  const locked = useOfflineSessionStore((s) => s.locked);
  const logoutPrompt = useOfflineSessionStore((s) => s.logoutPrompt);
  const [notice, setNotice] = useState(null);

  // A lock survives reload (otherwise a refresh would bypass it).
  useEffect(() => {
    if (isLockPersisted()) {
      lockIfOfflineMode('inactivity');
    }
  }, []);

  // Trust-window ticker: while offline, harden the session once the Xano
  // token passes its 24h life. (Online, the 23h proactive refresh in
  // UserContext keeps the token young and this never fires.)
  useEffect(() => {
    const timer = setInterval(() => {
      if (connectivityMonitor.isOnline()) return;
      const age = authService.getTokenAgeMs();
      if (age !== null && age >= TRUST_WINDOW_MS) {
        lockIfOfflineMode('trust-window-expired');
      }
    }, TICK_MS);
    return () => clearInterval(timer);
  }, []);

  // "Not available offline" notifications from blocked actions.
  useEffect(() => {
    const onUnavailable = (e) => {
      setNotice({ message: e.detail?.message || 'Not available offline', at: Date.now() });
    };
    window.addEventListener('offline:action-unavailable', onUnavailable);
    return () => window.removeEventListener('offline:action-unavailable', onUnavailable);
  }, []);

  return (
    <>
      {logoutPrompt && <OfflineLogoutConfirmDialog />}
      {notice && (
        <Notification
          key={notice.at}
          message={notice.message}
          type="error"
          duration={4000}
          onClose={() => setNotice(null)}
        />
      )}
      {locked && <OfflineLockScreen />}
    </>
  );
}
